import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import ContactUserSection from '../components/ContactUserSection';
import useOrderTable from '../hooks/useOrderTable';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import { formatCurrency } from '../utils/format';
import TableView from '../components/TableView';
import { useForm, Controller } from 'react-hook-form';
import OrderClientInfo from '../components/OrderClientInfo';
import LabelValueItem from '../components/LabelValueItem';
import { Paper, Grid, Typography, Button, TextField, InputAdornment, IconButton, Divider, Stack, Box, Snackbar, Alert, Tooltip } from '@mui/material';
import { Add, LibraryAdd, Delete } from '@mui/icons-material'
const EstimateNewPage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [selectedClient, setSelectedClient] = useState(null);
    const isEditMode = true;
    const [fieldErrors, setFieldErrors] = useState({
        items: []
    });
    // 新規見積データの管理
    const [orderData, setOrderData] = useState({
        estimate_number: '',
        branch_name: '',
        client_name: '',
        client_id: null,
        client_contact_id: null,
        client_contact_name: '',
        client_contact_phone: '',
        shipping_address_id: null,
        postal_code: '',
        state: '',
        address_line: '',
        delivery_date: null,
        recipient_name: '',
        payment_terms: '',
        payment_method_name: '',
        total_amount: 0,
        total_amount_including_tax: 0,
        items: [],
        estimate_notes: '',
        user_fullname: '',
        user_email: '',
        user_phone: '',
    });
    const { register, handleSubmit, control, formState: { errors }, setValue } = useForm({
        defaultValues: orderData,
    });
    const handleItemDelete = (index) => {
        const updatedItems = orderData.items.filter((_, i) => i !== index);
        setOrderData({ ...orderData, items: updatedItems });
    };
    const {
        columns,
        updateItem,
        handleItemChange,
    } = useOrderTable({
        control,
        errors,
        isEditMode,
        hasLimit,
        axiosInstance,
        selectedClient,
        orderData,
        setOrderData,
        calculateSellingPriceIncludingTax,
        calculateItemSubtotal,
        calculateTotalAmount,
        formatCurrency,
        handleItemDelete,
    });
    // 見積アイテムの追加
    const addItem = () => {
        const newItem = {
            product_id: null,
            variant_id: null,
            sku: '',
            price: 0,
            quantity: 1,
            selling_price: 0,
            selling_price_including_tax: 0,
            tax_rate: 0,
            subtotal: 0,
            subtotal_including_tax: 0,
        };

        const newItems = [...orderData.items, newItem];

        setOrderData({
            ...orderData,
            items: newItems,
        });
        setValue(`items.${newItems.length - 1}`, newItem);
    };
    // 新規見積の送信
    const onSubmit = async (data) => {
        const submitData = {
            ...orderData,
            ...data,
            estimate_date: null,
            items: orderData.items,
            total_amount: orderData.total_amount,
            total_amount_including_tax: orderData.total_amount_including_tax
        };
        try {
            // 見積データを送信
            const res = await axiosInstance.post('/estimate/new', { orderData: submitData });
            navigate(`/estimates/${res.data.estimate_id}`, {
                state: { snackbar: { message: res?.data?.message || '見積が作成されました。', severity: 'success' } }
            });
        } catch (err) {
            if (process.env.NODE_ENV === 'development') {
                console.error(err);
            }
            showSnackbar(err?.response?.data?.message || '見積作成に失敗しました。', 'error');
        }
    };

    return (
        <>
            <Typography variant="h1" component="h1">見積作成</Typography>
            <Paper variant='section'>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Link to="/products/new">商品登録</Link> を行ってから作成を開始しましょう。
                </Alert>
                <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><LabelValueItem label='見積日時' value='（作成日時）' /></Grid>
                        <Grid item xs={12} sm={6}><LabelValueItem label='見積番号' value='（自動採番）' /></Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />
                    <Typography component='h2' variant="h4" mb={2}>お客様情報</Typography>
                    <OrderClientInfo
                        hasLimit={hasLimit}
                        isEditMode={isEditMode}
                        orderData={orderData}
                        setOrderData={setOrderData}
                        register={register}
                        setValue={setValue}
                        control={control}
                        errors={errors}
                        fieldErrors={fieldErrors}
                        setFieldErrors={setFieldErrors}
                        setSelectedClient={setSelectedClient}
                        mode='estimate'
                    />
                    <Divider sx={{ my: 2 }} />
                    <TextField
                        label="備考"
                        multiline
                        minRows={3}
                        disabled={!isEditMode}
                        error={!!errors.estimate_notes}
                        helperText={errors.estimate_notes?.message}
                        {...register('estimate_notes', {
                            maxLength: {
                                value: 2000,
                                message: '2000文字以内で入力してください',
                            },
                        })}
                        inputProps={{ maxLength: 2000 }}
                        sx={{ mt: 2 }}
                    />
                    <Divider sx={{ my: 2 }} />
                    <Typography component='h2' variant="h4">商品情報</Typography>
                    <Alert severity="info">
                        <Link to="/products/new">商品登録</Link>した品番から検索し、商品を選択してください。
                    </Alert>
                    <TableView dataset={orderData.items} columns={columns} rowClass="edit-container" keyColumn={(row, index) => index} />
                    {fieldErrors.items && <span className="error-message font-s">{fieldErrors.items}<br /></span>}
                    <Button
                        type="button"
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addItem}
                        size='small'
                        sx={{ mt: 1 }}
                    >アイテムを追加</Button>
                    {/* 合計金額 */}
                    <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
                        <Stack spacing={1}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                                <Typography fontWeight="bold">{formatCurrency(orderData.total_amount)}</Typography>
                            </Stack>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                <Typography fontWeight="bold">合計金額（税込）</Typography>
                                <Typography fontSize={22} fontWeight="bold">{formatCurrency(orderData.total_amount_including_tax)}</Typography>
                            </Stack>
                            <Divider />
                        </Stack>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <ContactUserSection
                        title="見積担当者"
                        isEditMode={isEditMode}
                        control={control}
                        register={register}
                        setValue={setValue}
                        errors={errors}
                    />
                    <div className='button-row'>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<LibraryAdd />}
                        >
                            見積を作成
                        </Button>
                    </div>
                </form>
            </Paper>
        </>
    );
};

export default EstimateNewPage;
